<template>
  <div class="modal-card">
    <b-loading :isFullPage="false" :active="isLoading" />
    <header class="modal-card-head">
      <p class="modal-card-title is-size-5 has-text-centered">
        {{ General.REQUEST_SUBSCRIPTION }}
      </p>
      <div class="close-button cursor-pointer" @click.stop="close(false)">
        <b-icon icon="close" />
      </div>
    </header>
    <div class="modal-content-container">
      <section class="spacing-p-5">
        <div class="custom-right">
          <b-button
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            @click="openModal"
          >
            {{ General.EXTERN_SIGNATURE }}
          </b-button>
        </div>
        <b-field customClass="has-text-color-7" :label="General.ORGAN_ENTITY">
          <b-autocomplete
            v-model="entityName"
            :placeholder="General.PLACEHOLDER.SEARCH_ORGAN_ENTITY"
            keepFirst
            :openOnFocus="openOnFocus"
            :data="filteredParts(entityName)"
            field="name"
            @select="option => (selectedEntity = option)"
          />
        </b-field>

        <b-field>
          <template slot="label">
            <span class="has-text-color-7">
              {{ General.SUBSCRIBER }}
            </span>
          </template>
        </b-field>
        <b-field grouped expanded>
          <b-autocomplete
            ref="subscriberInput"
            class="overflow-list"
            :value="subscriberName"
            :disabled="!selectedEntity"
            :placeholder="General.PLACEHOLDER.SEARCH_USERNAME"
            :loading="isFetching"
            keepFirst
            :openOnFocus="openOnFocus"
            :data="getUsersFromSearchFormat"
            field="displayName"
            :maxHeight="100"
            expanded
            @typing="filteredSubscriberData"
            @select="selectSubscriber"
          />
        </b-field>

        <draggable
          v-if="subscribersList.length > 0"
          v-model="subscribersList"
          :disabled="!followSubscriptionOrder"
        >
          <CustomSubscriberList
            v-for="(item, index) in subscribersList"
            :key="index"
            :subscriber="item"
          />
        </draggable>

        <div class="field pt-10">
          <b-checkbox
            v-model="followSubscriptionOrder"
            :type="isContrast ? 'is-white-dark' : 'is-primary'"
            class="has-text-color-7 pr-15"
          >
            {{ General.FOLLOW_SUBSCRIPTION_ORDER }}
          </b-checkbox>
        </div>
        <div class="spacing-py-5"></div>
      </section>
    </div>
    <footer class="modal-card-foot is-justify-content-flex-end">
      <b-button
        :type="isContrast ? 'is-white-dark' : 'is-primary'"
        :disabled="!subscribersList.length"
        @click="sendSubscribers"
      >
        {{ General.SUBMIT.toUpperCase() }}
      </b-button>
      <b-button
        :type="isContrast ? 'is-white-dark' : 'is-primary'"
        outlined
        @click="close(false)"
      >
        {{ General.CANCEL.toUpperCase() }}
      </b-button>
    </footer>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex";
import debounce from "lodash/debounce";
import draggable from "vuedraggable";

import CustomSubscriberList from "@/components/CustomSubscriberList";
import { App, General } from "@/utils/constants";
import ModalExternalAssign from "@/views/App/ModalExternalAssign.vue";
import { listHasDuplicates } from "@/utils/dataManipulation";

export default {
  name: "ModalDocSignature",

  components: {
    CustomSubscriberList,
    draggable
  },

  props: {
    updateFiles: {
      type: Function,
      default: null
    },

    dispatchId: {
      type: String,
      default: null
    },

    processId: {
      type: String,
      default: null
    },

    joinId: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      App,
      General,
      isLoading: false,
      openOnFocus: false,
      entityName: "",
      subscriberName: "",
      followSubscriptionOrder: false,
      selectedEntity: null,
      selectedSubscriber: null,
      subscribers: [],
      isSending: false,
      isFetching: false
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      getUsersFromSearch: "getUsersFromSearch",
      getSubscribers: "getSubscribers",
      getSubscribersError: "getSubscribersError",
      getCurrentProcessFileId: "getCurrentProcessFileId",
      filteredParts: "getFilteredParts",
      getPartsById: "getPartsById",
      batchFiles: "getCurrentProcessFileList",
      isContrast: "isContrast"
    }),
    subscribersList: {
      get() {
        return this.getSubscribers;
      },
      set(value) {
        const newList = value.map((subscriber, index) => ({
          ...subscriber,
          order: index
        }));
        this.$store.commit("updateSubscriderOrder", newList);
      }
    },

    getUsersFromSearchFormat() {
      return this.getUsersFromSearch
        .filter(
          user =>
            process.env.VUE_APP_ENVIRONMENT === "development" ||
            user.id !== this.user.id
        )
        .map(user => ({
          ...user,
          displayName: `${user.name}${
            user.capacity_selected
              ? " - " + user.capacity_selected.abbreviation
              : ""
          }`
        }));
    }
  },

  beforeMount() {
    this.clearError();
    this.$store.commit("resetSubscriberStore");
    this.selectedEntity = this.getPartsById(this.user.capacity.part_id);
    this.entityName = this.selectedEntity.name;
  },

  mounted() {
    this.$refs.subscriberInput.focus();
  },

  methods: {
    ...mapActions({
      userFilter: "userGenericFilterRequest",
      postSubscriberRequest: "postSubscriberRequest",
      postExternalSubscriberRequest: "postExternalSubscriberRequest"
    }),

    ...mapMutations({
      clearError: "clearError",
      setBatchFiles: "setCurrentProcessFileList"
    }),

    selectSubscriber(selectedSubscriber) {
      this.clearError();
      if (selectedSubscriber) {
        this.$store.commit("addSubscriber", selectedSubscriber);
        this.subscriberName = "";
      }
    },

    close(update = true) {
      this.$parent.close();
      this.$store.commit("resetSubscriberStore");
      if (update && this.updateFiles) this.updateFiles();
    },

    async sendSubscribers() {
      this.isLoading = true;
      if (this.hasDuplicatedCpf()) {
        this.snackToast.open({
          message: General.MESSAGES.ALERT_REQUEST_SIGNATURE_DUPLICATED,
          type: "is-danger",
          duration: 8000
        });
        this.isLoading = false;
        return;
      }

      const internalSubscribers = this.prepareSubscribers();
      const externalSubscribers = this.prepareExternalSubscribers();

      internalSubscribers.length &&
        (await this.postSubscriberRequest({
          subscribers: internalSubscribers,
          hasOrder: this.followSubscriptionOrder,
          processId: this.processId,
          joinId: this.batchFiles[0].document_join_id
        }));

      externalSubscribers.length &&
        (await this.postExternalSubscriberRequest({
          subscriber: externalSubscribers,
          processId: this.processId
        }));

      if (!this.getSubscribersError) {
        this.setBatchFiles([]);
        await this.$store.commit("setCurrentProcessFileId", null);
        this.close();
      }
      this.isLoading = false;
    },

    hasDuplicatedCpf() {
      const cpfList = this.subscribersList.map(sub => sub.cpf || sub.cpf_cnpj);
      return listHasDuplicates(cpfList);
    },

    prepareSubscribers() {
      const internal = this.subscribersList.filter(sub => !sub.isExternal);

      return this.batchFiles.map(process_file =>
        internal.map(sub => ({
          user_id: sub.id,
          capacity_id: sub.capacity_selected.id,
          process_file_id: process_file.id,
          order: sub.order
        }))
      );
    },

    prepareExternalSubscribers() {
      const usersExternal = this.subscribersList.filter(sub => sub.isExternal);
      return this.batchFiles.reduce((acc, current) => {
        acc.push(
          ...usersExternal.map(sub => ({
            process_file_id: current.id,
            email: sub.email,
            name: sub.name,
            cpf_cnpj: sub.cpf_cnpj
          }))
        );
        return acc;
      }, []);
    },

    filteredSubscriberData: debounce(async function(name) {
      this.subscriberName = name;

      if (name === "") {
        this.$store.commit("setUsersFromSearch", []);
        return;
      }
      this.isFetching = true;
      await this.userFilter({
        part_id: this.selectedEntity.id,
        name
      });
      this.isFetching = false;
    }, 500),

    addExternalAssign(newUser) {
      this.subscribersList.push({ ...newUser, bonds: [] });
    },

    openModal() {
      this.$buefy.modal.open({
        parent: this,
        props: {
          onClose: this.addExternalAssign
        },
        component: ModalExternalAssign,
        hasModalCard: true,
        trapFocus: true
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";
.modal-card {
  min-width: 600px;
  max-height: 75vh;
  height: 100%;
  background-color: $color-1;

  @media screen and (max-width: $tablet) {
    min-width: auto;
  }
}

.modal-card-body {
  padding: 0 15px 15px 15px;
}

.modal-content-container {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
  overflow: auto;
  padding: 10px 15px 0;
}

.custom-right {
  justify-content: flex-end;
  display: flex;
}
</style>
